// Contact.jsx
import "./Contact.css";
import { useState } from "react";
import {
  FaFacebookF,
  FaInstagram,
  FaWhatsapp,
  FaYoutube,
  FaLinkedinIn,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCalendarCheck,
  FaArrowRight,
  FaChevronDown,
  FaClock,
  FaUsers,
  FaGift,
  FaGamepad,
} from "react-icons/fa";
import {
  FaXTwitter,
  FaCircleQuestion,
  FaMasksTheater,
} from "react-icons/fa6";

const WHATSAPP_NUMBER = "919566137290";
const HOTLINE = "9087775659";
const CONTACT_EMAIL = "contact@thehexaplay.com";
const BOOKING_URL = "/book-now/";
const NAVALUR_URL = "/locations/navalur/";

const NAVALUR_MAP_URL =
  "https://www.google.com/maps/search/?api=1&query=HexaPlay+Navalur+OMR+Chennai";

const faqGroups = [
  {
    title: "Booking & Availability",
    icon: <FaCalendarCheck />,
    questions: [
      {
        q: "How can I book an experience at HexaPlay?",
        a: "You can book through our booking page, WhatsApp or phone. Share your occasion, preferred date, time and guest count, and our team will confirm availability.",
      },
      {
        q: "How early should I book?",
        a: "We recommend booking in advance for birthdays, anniversaries, proposals and weekends. Contact our team to check availability for your preferred date. The recommended advance-booking period should be confirmed by the business.",
      },
      {
        q: "Can I check availability before booking?",
        a: "Yes. Share your preferred date, time, experience and guest count, and our team will check availability.",
      },
      {
        q: "Can I make a last-minute booking?",
        a: "Possibly. Last-minute bookings depend on available slots, so contact the team before making plans.",
      },
    ],
  },
  {
    title: "Pricing & Packages",
    icon: <FaCircleQuestion />,
    questions: [
      {
        q: "How much does a HexaPlay experience cost?",
        a: "Pricing depends on the experience, time slot, number of guests and selected add-ons. Contact our team for current prices and package details.",
      },
      {
        q: "Can I create a customized package?",
        a: "Yes. You can discuss your occasion and preferred add-ons with our team to plan a package around the available options.",
      },
      {
        q: "Do add-ons cost extra?",
        a: "Yes. Add-ons such as cakes, food, gifts and special experiences may have separate charges. Confirm the final price with the team before booking.",
      },
      {
        q: "Is there a minimum or maximum number of guests?",
        a: "Guest limits depend on the selected experience and venue setup. Please contact the team to confirm the permitted guest count before booking.",
      },
    ],
  },
  {
    title: "Celebrations & Add-Ons",
    icon: <FaGift />,
    questions: [
      {
        q: "What celebrations can I plan at HexaPlay?",
        a: "You can enquire about birthdays, anniversaries, date nights, proposals, special surprises, bride-to-be celebrations, kids’ celebrations and first birthdays.",
      },
      {
        q: "What add-ons are available?",
        a: "Available options may include cakes, food and beverages, bouquets, gifts, fog entry, canvas painting, customized songs and karaoke. Ask the team to confirm current availability.",
      },
      {
        q: "Can I customize the theatre decoration?",
        a: "No. All three private theatres have fixed, pre-installed premium decor that cannot be customized or altered. The space is prepared with its existing decor before you arrive.",
      },
      {
        q: "Can I plan a surprise or proposal?",
        a: "Yes. Our team can help you plan a surprise or proposal using the available celebration options and add-ons.",
      },
    ],
  },
  {
    title: "Private Theatre",
    icon: <FaMasksTheater />,
    questions: [
      {
        q: "Is the theatre experience private?",
        a: "Yes. The private theatre is reserved for your group during your booked slot.",
      },
      {
        q: "Can I watch my own movie or OTT content?",
        a: "Please discuss your movie or OTT requirements with our team before booking so they can confirm the supported options and any applicable conditions.",
      },
      {
        q: "Can I celebrate a birthday or anniversary in the theatre?",
        a: "Yes. A private theatre session can be part of a birthday, anniversary or another special celebration.",
      },
      {
        q: "Can I combine the theatre with add-ons?",
        a: "Yes. You can enquire about adding cakes, food, gifts and other available extras to the pre-decorated theatre experience.",
      },
    ],
  },
  {
    title: "Gaming",
    icon: <FaGamepad />,
    questions: [
      {
        q: "Does HexaPlay offer gaming?",
        a: "Yes. Gaming is one of the entertainment experiences you can enquire about at HexaPlay.",
      },
      {
        q: "Can I visit only for gaming?",
        a: "Gaming-only visits depend on the current setup and availability. Contact our team to confirm whether a separate gaming session can be booked.",
      },
      {
        q: "Can friends or groups play together?",
        a: "Yes, group gaming may be possible depending on the available setup, game and guest count. Confirm the details with our team.",
      },
      {
        q: "Can I add gaming to a celebration?",
        a: "Yes. Gaming may be combined with selected celebration experiences, subject to availability.",
      },
    ],
  },
  {
    title: "Kids & Family",
    icon: <FaUsers />,
    questions: [
      {
        q: "Can I plan a kids’ celebration at HexaPlay?",
        a: "Yes. You can enquire about kids’ birthdays and other special occasion celebrations.",
      },
      {
        q: "Can I celebrate my child’s first birthday?",
        a: "Yes. You can enquire about a first birthday in a pre-decorated private theatre and discuss suitable add-ons with the team.",
      },
      {
        q: "Is HexaPlay suitable for families?",
        a: "Yes. Families can enquire about celebration, entertainment and gaming experiences suited to their group and requirements.",
      },
      {
        q: "Can children play games?",
        a: "Children's access depends on the game, equipment and applicable age restrictions. Please confirm age limits and supervision requirements with the team.",
      },
    ],
  },
  {
    title: "Timing & Location",
    icon: <FaMapMarkerAlt />,
    questions: [
      {
        q: "Where is HexaPlay located?",
        a: "HexaPlay currently has a location in Navalur, along OMR in Chennai. Visit the Navalur location page for the latest address and map details.",
      },
      {
        q: "What are the operating hours?",
        a: "Operating hours can vary by branch and booking schedule. Please contact the team to confirm the current hours for Navalur.",
      },
      {
        q: "How can I get directions?",
        a: "Use the Get Directions button on this page to open a Google Maps search for HexaPlay Navalur. Confirm the exact destination before travelling.",
      },
      {
        q: "Is parking available?",
        a: "Parking availability has not been confirmed here. Contact the Navalur team before your visit to check the current parking arrangements.",
      },
    ],
  },
  {
    title: "Cancellation & Changes",
    icon: <FaClock />,
    questions: [
      {
        q: "Can I cancel my booking?",
        a: "Cancellations are subject to the applicable booking and refund terms. Contact the team and review the published cancellation policy before confirming a booking.",
      },
      {
        q: "Can I reschedule my booking?",
        a: "Rescheduling depends on slot availability and the applicable booking terms. Contact the team as early as possible to discuss a change.",
      },
      {
        q: "What if I need to change my booking details?",
        a: "Contact the team as early as possible with your booking details so they can check the available options.",
      },
      {
        q: "What if there is a technical issue?",
        a: "Inform the team immediately during your visit so they can assess the issue and assist you.",
      },
    ],
  },
  {
    title: "Photos, Videos & Collaboration",
    icon: <FaInstagram />,
    questions: [
      {
        q: "Can I take photos and videos during my celebration?",
        a: "Please discuss your photography and video requirements with the team before booking so they can confirm the current rules and available options.",
      },
      {
        q: "Do you provide video support?",
        a: "Video support depends on the specific requirement and available options. Contact the team to discuss your needs before booking.",
      },
      {
        q: "Can I enquire about collaborations?",
        a: "Yes. Brand, influencer and event collaboration enquiries can be shared with the HexaPlay team through the contact form or WhatsApp.",
      },
      {
        q: "Can I enquire without booking immediately?",
        a: "Yes. You can contact us first to understand the experiences, pricing, add-ons and availability before deciding whether to book.",
      },
    ],
  },
];

function Contact() {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    occasion: "",
    preferredDate: "",
    guestCount: "",
    message: "",
  });

  const [openFaqs, setOpenFaqs] = useState({});
  const [activeFaqCategory, setActiveFaqCategory] = useState("All");

  const handleChange = (event) => {
    const { name } = event.target;
    let { value } = event.target;

    if (name === "phone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const {
      fullName,
      phone,
      occasion,
      preferredDate,
      guestCount,
      message,
    } = formData;

    if (!/^[6-9]\d{9}$/.test(phone)) {
      alert("Enter a valid 10-digit mobile number starting with 6-9.");
      return;
    }

    const whatsappMessage = [
      "New Contact Enquiry — HexaPlay",
      "",
      `Name: ${fullName.trim()}`,
      `Phone: +91 ${phone}`,
      `Occasion / Experience: ${occasion || "Not specified"}`,
      `Preferred Date: ${preferredDate || "Not specified"}`,
      `Guest Count: ${guestCount || "Not specified"}`,
      ...(message.trim() ? [`Message: ${message.trim()}`] : []),
    ].join("\n");

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      whatsappMessage
    )}`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const toggleFaq = (faqKey) => {
    setOpenFaqs((previous) => ({
      ...previous,
      [faqKey]: !previous[faqKey],
    }));
  };

  const visibleFaqGroups =
    activeFaqCategory === "All"
      ? faqGroups
      : faqGroups.filter((group) => group.title === activeFaqCategory);

  return (
    <main className="contact-page">
      <section className="contact-hero">
        <div className="contact-hero-glow contact-hero-glow-one" />
        <div className="contact-hero-glow contact-hero-glow-two" />

        <div className="contact-hero-content">
          <span className="contact-eyebrow">
            LET&apos;S PLAN SOMETHING SPECIAL
          </span>

          <h1 className="contact-title">Contact HexaPlay</h1>

          <h2 className="contact-hero-subtitle">
            Have a Question? We&apos;re Here to Help
          </h2>

          <p className="contact-hero-description">
            Planning a celebration, private theatre experience, gaming session
            or outdoor event? Contact the HexaPlay team to enquire about
            availability, pricing, services, add-ons and customization. Share
            your requirements and we&apos;ll help you plan the right
            experience.
          </p>

          <div className="contact-hero-actions">
            <a
              className="contact-btn contact-btn-primary"
              href={BOOKING_URL}
            >
              <span className="contact-btn-icon" aria-hidden="true">
                <FaCalendarCheck />
              </span>
              <span className="contact-btn-label">Book Your Experience</span>
              <span
                className="contact-btn-icon contact-btn-arrow"
                aria-hidden="true"
              >
                <FaArrowRight />
              </span>
            </a>

            <a
              className="contact-btn contact-btn-whatsapp"
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                "Hi HexaPlay, I would like to enquire about your experiences and availability."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="contact-btn-icon" aria-hidden="true">
                <FaWhatsapp />
              </span>
              <span className="contact-btn-label">WhatsApp Us</span>
            </a>

            <a
              className="contact-btn contact-btn-outline"
              href={NAVALUR_MAP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="contact-btn-icon" aria-hidden="true">
                <FaMapMarkerAlt />
              </span>
              <span className="contact-btn-label">
                Get Directions (Navalur)
              </span>
            </a>
          </div>

          <div className="contact-hero-note">
            <span className="contact-note-dot" />
            <span>
              Enquire first, compare options, and book when you&apos;re ready.
            </span>
          </div>
        </div>

        <div className="contact-hero-decoration" aria-hidden="true">
          <div className="contact-decoration-card contact-decoration-card-back">
            <FaGamepad />
            <span>Play</span>
          </div>

          <div className="contact-decoration-card contact-decoration-card-front">
            <FaGift />
            <span>Celebrate</span>
          </div>

          <div className="contact-decoration-orbit" />
        </div>
      </section>

      <section className="contact-quick-strip" aria-label="Contact options">
        <a href={`tel:+91${HOTLINE}`} className="contact-quick-item">
          <span className="contact-quick-icon" aria-hidden="true">
            <FaPhoneAlt />
          </span>

          <span className="contact-quick-copy">
            <strong>Call our team</strong>
            <small>+91 {HOTLINE}</small>
          </span>

          <FaArrowRight
            className="contact-quick-arrow"
            aria-hidden="true"
          />
        </a>

        <a href={`mailto:${CONTACT_EMAIL}`} className="contact-quick-item">
          <span className="contact-quick-icon" aria-hidden="true">
            <FaEnvelope />
          </span>

          <span className="contact-quick-copy">
            <strong>Email your enquiry</strong>
            <small>{CONTACT_EMAIL}</small>
          </span>

          <FaArrowRight
            className="contact-quick-arrow"
            aria-hidden="true"
          />
        </a>

        <a href={NAVALUR_URL} className="contact-quick-item">
          <span className="contact-quick-icon" aria-hidden="true">
            <FaMapMarkerAlt />
          </span>

          <span className="contact-quick-copy">
            <strong>Visit our branch</strong>
            <small>HexaPlay Navalur, OMR</small>
          </span>

          <FaArrowRight
            className="contact-quick-arrow"
            aria-hidden="true"
          />
        </a>
      </section>

      <section className="contact-main-section">
        <div className="contact-section-heading">
          <span className="contact-eyebrow">GET IN TOUCH</span>
          <h2>Tell Us What You&apos;re Planning</h2>
          <p>
            Share a few details about your occasion. Our contact form prepares
            an enquiry message for WhatsApp so you can send it directly to our
            team.
          </p>
        </div>

        <div className="contact-container">
          <form className="contact-form-card" onSubmit={handleSubmit}>
            <div className="contact-form-heading">
              <div className="contact-form-heading-copy">
                <span className="contact-form-step">YOUR ENQUIRY</span>
                <h3>Send us a message</h3>
                <p>
                  Complete the details below and continue securely on WhatsApp.
                </p>
              </div>

              <span
                className="contact-form-heading-icon"
                aria-hidden="true"
              >
                <FaEnvelope />
              </span>
            </div>

            <div className="contact-form-grid">
              <div className="form-group form-half">
                <label htmlFor="fullName">Full Name *</label>
                <input
                  id="fullName"
                  type="text"
                  name="fullName"
                  placeholder="Enter your full name"
                  autoComplete="name"
                  required
                  value={formData.fullName}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group form-half">
                <label htmlFor="phone">Mobile Number *</label>

                <div className="phone-input">
                  <span className="phone-prefix" aria-hidden="true">
                    +91
                  </span>

                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    placeholder="10-digit mobile number"
                    autoComplete="tel-national"
                    inputMode="numeric"
                    required
                    pattern="[6-9][0-9]{9}"
                    maxLength={10}
                    value={formData.phone}
                    onChange={handleChange}
                    onInvalid={(event) => {
                      event.target.setCustomValidity(
                        "Enter a valid 10-digit mobile number starting with 6-9."
                      );
                    }}
                    onInput={(event) =>
                      event.target.setCustomValidity("")
                    }
                  />
                </div>
              </div>

              <div className="form-group form-third">
                <label htmlFor="occasion">Experience / Occasion</label>
                <select
                  id="occasion"
                  name="occasion"
                  value={formData.occasion}
                  onChange={handleChange}
                >
                  <option value="">Select an experience</option>
                  <option value="Birthday celebration">
                    Birthday celebration
                  </option>
                  <option value="Anniversary">Anniversary</option>
                  <option value="Date night">Date night</option>
                  <option value="Proposal or surprise">
                    Proposal or surprise
                  </option>
                  <option value="Private theatre">Private theatre</option>
                  <option value="Gaming">Gaming</option>
                  <option value="Kids celebration">
                    Kids&apos; celebration
                  </option>
                  <option value="Outdoor event">Outdoor event</option>
                  <option value="Collaboration">
                    Brand / event collaboration
                  </option>
                  <option value="Other enquiry">Other enquiry</option>
                </select>
              </div>

              <div className="form-group form-third">
                <label htmlFor="preferredDate">Preferred Date</label>
                <input
                  id="preferredDate"
                  type="date"
                  name="preferredDate"
                  min={new Date().toLocaleDateString("en-CA")}
                  value={formData.preferredDate}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group form-third">
                <label htmlFor="guestCount">Expected Guests</label>
                <select
                  id="guestCount"
                  name="guestCount"
                  value={formData.guestCount}
                  onChange={handleChange}
                >
                  <option value="">Select guest count</option>
                  <option value="1–2 guests">1–2 guests</option>
                  <option value="3–5 guests">3–5 guests</option>
                  <option value="6–10 guests">6–10 guests</option>
                  <option value="11–15 guests">11–15 guests</option>
                  <option value="More than 15 guests">
                    More than 15 guests
                  </option>
                  <option value="Not sure yet">Not sure yet</option>
                </select>
              </div>

              <div className="form-group form-full">
                <label htmlFor="message">
                  <span>How Can We Help?</span>
                  <span className="form-optional">Optional</span>
                </label>

                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about your occasion, preferred time, add-ons or any special requirements..."
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="contact-form-footer">
              <div className="contact-form-privacy">
                <span
                  className="contact-form-privacy-icon"
                  aria-hidden="true"
                >
                  <FaWhatsapp />
                </span>

                <span>
                  Selecting Send Enquiry opens WhatsApp with your details
                  filled in. Review the information before sending it to our
                  team.
                </span>
              </div>

              <button type="submit" className="send-btn">
                <span className="send-btn-icon" aria-hidden="true">
                  <FaWhatsapp />
                </span>

                <span className="send-btn-label">
                  Send Enquiry on WhatsApp
                </span>

                <span
                  className="send-btn-icon send-btn-arrow"
                  aria-hidden="true"
                >
                  <FaArrowRight />
                </span>
              </button>
            </div>
          </form>

          <aside className="contact-info-card">
            <div className="contact-info-top">
              <span className="contact-info-label">
                WE&apos;RE HAPPY TO HELP
              </span>

              <h3>
                Your next
                <br />
                great memory
                <br />
                starts here.
              </h3>

              <p className="contact-info-intro">
                Have questions about a package, slot or special request?
                Reach out to our team and tell us what you have in mind.
              </p>
            </div>

            <div className="contact-info-links">
              <a
                className="contact-info-link"
                href={`tel:+91${HOTLINE}`}
              >
                <span className="contact-info-icon" aria-hidden="true">
                  <FaPhoneAlt />
                </span>

                <span className="contact-info-link-copy">
                  <small>Call our hotline</small>
                  <strong>+91 {HOTLINE}</strong>
                </span>

                <FaArrowRight
                  className="contact-info-arrow"
                  aria-hidden="true"
                />
              </a>

              <a
                className="contact-info-link"
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="contact-info-icon" aria-hidden="true">
                  <FaWhatsapp />
                </span>

                <span className="contact-info-link-copy">
                  <small>Chat on WhatsApp</small>
                  <strong>Message our team</strong>
                </span>

                <FaArrowRight
                  className="contact-info-arrow"
                  aria-hidden="true"
                />
              </a>

              <a
                className="contact-info-link"
                href={`mailto:${CONTACT_EMAIL}`}
              >
                <span className="contact-info-icon" aria-hidden="true">
                  <FaEnvelope />
                </span>

                <span className="contact-info-link-copy">
                  <small>Email us</small>
                  <strong>{CONTACT_EMAIL}</strong>
                </span>

                <FaArrowRight
                  className="contact-info-arrow"
                  aria-hidden="true"
                />
              </a>
            </div>

            <div className="contact-info-support">
              <span
                className="contact-info-support-icon"
                aria-hidden="true"
              >
                <FaClock />
              </span>

              <span>
                <strong>Planning support</strong>
                <small>
                  Share your date and guest count for a quicker response.
                </small>
              </span>
            </div>

            <div className="contact-info-social">
              <div className="contact-info-divider" />

              <p className="social-title">Connect with HexaPlay</p>

              <div className="social-icons">
                <a
                  href="https://www.facebook.com/profile.php?id=61587483247837"
                  className="icon facebook"
                  aria-label="HexaPlay on Facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaFacebookF />
                </a>

                <a
                  href="https://www.instagram.com/thehexaplay/"
                  className="icon instagram"
                  aria-label="HexaPlay on Instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaInstagram />
                </a>

                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  className="icon whatsapp"
                  aria-label="HexaPlay on WhatsApp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaWhatsapp />
                </a>

                <a
                  href="https://x.com/thehexaplay?s=11"
                  className="icon twitter"
                  aria-label="HexaPlay on X"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaXTwitter />
                </a>

                <a
                  href="https://www.youtube.com/@thehexaplay"
                  className="icon youtube"
                  aria-label="HexaPlay on YouTube"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaYoutube />
                </a>

                <a
                  href="https://www.linkedin.com/company/thehexaplay/about/"
                  className="icon linkedin"
                  aria-label="HexaPlay on LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaLinkedinIn />
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="contact-branches-section">
        <div className="contact-branches-inner">
          <div className="contact-branches-copy">
            <span className="contact-eyebrow">FIND YOUR BRANCH</span>
            <h2>Our Branches</h2>
            <p>
              Select your branch for its address, contact details, timings and
              directions. Our current listed location is Navalur, OMR,
              Chennai.
            </p>
          </div>

          <article className="contact-branch-card">
            <div className="contact-branch-card-top">
              <span className="contact-branch-icon" aria-hidden="true">
                <FaMapMarkerAlt />
              </span>

              <span className="contact-branch-status">
                Current location
              </span>
            </div>

            <h3>HexaPlay Navalur</h3>
            <p>Navalur, OMR, Chennai, Tamil Nadu</p>

            <div className="contact-branch-actions">
              <a href={NAVALUR_URL} className="contact-branch-link">
                <span>Branch Details</span>
                <FaArrowRight aria-hidden="true" />
              </a>

              <a
                href={NAVALUR_MAP_URL}
                className="contact-branch-map"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Directions
              </a>
            </div>
          </article>
        </div>
      </section>

      <section className="contact-faq-section" id="contact-faq">
        <div className="contact-faq-inner">
          <div className="contact-section-heading">
            <span className="contact-eyebrow">HELP &amp; INFORMATION</span>
            <h2>Frequently Asked Questions</h2>
            <p>
              Find answers about bookings, packages, celebrations, private
              theatres, gaming, location and more. If you need more details,
              contact our team directly.
            </p>
          </div>

          <div
            className="contact-faq-filters"
            aria-label="Filter FAQ categories"
          >
            <button
              type="button"
              className={`contact-faq-filter ${
                activeFaqCategory === "All" ? "active" : ""
              }`}
              onClick={() => setActiveFaqCategory("All")}
            >
              All Questions
            </button>

            {faqGroups.map((group) => (
              <button
                type="button"
                key={group.title}
                className={`contact-faq-filter ${
                  activeFaqCategory === group.title ? "active" : ""
                }`}
                onClick={() => setActiveFaqCategory(group.title)}
              >
                {group.title}
              </button>
            ))}
          </div>

          <div className="contact-faq-groups">
            {visibleFaqGroups.map((group, groupIndex) => (
              <section
                className="contact-faq-category"
                key={group.title}
              >
                <div className="contact-faq-category-heading">
                  <span
                    className="contact-faq-category-icon"
                    aria-hidden="true"
                  >
                    {group.icon}
                  </span>

                  <h3>{group.title}</h3>
                </div>

                <div className="contact-faq-list">
                  {group.questions.map((faq, faqIndex) => {
                    const faqKey = `${group.title}-${faqIndex}`;
                    const isOpen = Boolean(openFaqs[faqKey]);

                    return (
                      <article
                        className={`contact-faq-item ${
                          isOpen ? "is-open" : ""
                        }`}
                        key={faq.q}
                      >
                        <h4>
                          <button
                            type="button"
                            className="contact-faq-question"
                            aria-expanded={isOpen}
                            aria-controls={`faq-answer-${groupIndex}-${faqIndex}`}
                            onClick={() => toggleFaq(faqKey)}
                          >
                            <span>{faq.q}</span>
                            <FaChevronDown
                              className="contact-faq-chevron"
                              aria-hidden="true"
                            />
                          </button>
                        </h4>

                        <div
                          className="contact-faq-answer"
                          id={`faq-answer-${groupIndex}-${faqIndex}`}
                          hidden={!isOpen}
                        >
                          <p>{faq.a}</p>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          <div className="contact-faq-help">
            <div
              className="contact-faq-help-icon"
              aria-hidden="true"
            >
              <FaCircleQuestion />
            </div>

            <div className="contact-faq-help-copy">
              <h3>Still have a question?</h3>
              <p>
                Tell us what you need and our team will help you understand
                the available options.
              </p>
            </div>

            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                "Hi HexaPlay, I have a question about your experiences."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-faq-help-button"
            >
              <span>Ask on WhatsApp</span>
              <FaArrowRight aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      <section className="contact-final-cta">
        <div className="contact-final-cta-content">
          <span className="contact-eyebrow">MAKE IT MEMORABLE</span>
          <h2>Let&apos;s Make Your Occasion Special</h2>

          <p>
            From intimate celebrations to fun-filled gaming sessions, tell us
            your plans and let&apos;s explore the right HexaPlay experience
            for you.
          </p>

          <div className="contact-final-cta-actions">
            <a
              href={BOOKING_URL}
              className="contact-btn contact-btn-primary"
            >
              <span className="contact-btn-icon" aria-hidden="true">
                <FaCalendarCheck />
              </span>

              <span className="contact-btn-label">
                Book Your Experience
              </span>

              <span
                className="contact-btn-icon contact-btn-arrow"
                aria-hidden="true"
              >
                <FaArrowRight />
              </span>
            </a>

            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                "Hi HexaPlay, I would like help planning an experience."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-btn contact-btn-light"
            >
              <span className="contact-btn-icon" aria-hidden="true">
                <FaWhatsapp />
              </span>

              <span className="contact-btn-label">Talk to Our Team</span>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Contact;